import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/lib/index';
import { ROUTE_PATHS } from '@/lib/index';
import { newsArticles, formatDate, type Language } from '@/data/newsData';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const uiStrings: Record<Language, {
  badge: string;
  heroTitle: string;
  heroSubtitle: string;
  breadcrumbHome: string;
  breadcrumbCurrent: string;
  readMore: string;
}> = {
  'zh-CN': {
    badge: '公司动态',
    heroTitle: '最新动态',
    heroSubtitle: '了解 SHONCHIN MANOR & CO. 的最新资讯、行业洞察与重要里程碑。',
    breadcrumbHome: '首页',
    breadcrumbCurrent: '公司动态',
    readMore: '阅读全文',
  },
  'zh-TW': {
    badge: '公司動態',
    heroTitle: '最新動態',
    heroSubtitle: '了解 SHONCHIN MANOR & CO. 的最新資訊、行業洞察與重要里程碑。',
    breadcrumbHome: '首頁',
    breadcrumbCurrent: '公司動態',
    readMore: '閱讀全文',
  },
  'en': {
    badge: 'COMPANY NEWS',
    heroTitle: 'Latest News & Updates',
    heroSubtitle: 'Stay informed on the latest developments, industry insights, and key milestones from SHONCHIN MANOR & CO.',
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Company News',
    readMore: 'Read More',
  },
  'ja': {
    badge: '会社ニュース',
    heroTitle: '最新ニュース',
    heroSubtitle: 'SHONCHIN MANOR & CO. の最新情報、業界インサイト、重要なマイルストーンをお届けします。',
    breadcrumbHome: 'ホーム',
    breadcrumbCurrent: '会社ニュース',
    readMore: '続きを読む',
  },
  'ko': {
    badge: '회사 소식',
    heroTitle: '최신 소식',
    heroSubtitle: 'SHONCHIN MANOR & CO.의 최신 동향, 업계 인사이트, 주요 이정표를 확인하세요.',
    breadcrumbHome: '홈',
    breadcrumbCurrent: '회사 소식',
    readMore: '더 보기',
  },
};

export default function CompanyNews() {
  const { language } = useLanguage();
  const lang = (language as Language) in uiStrings ? (language as Language) : 'en';
  const ui = uiStrings[lang];

  // Sort articles oldest-first (earliest date at the top)
  const sorted = [...newsArticles].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative bg-foreground text-background py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-primary blur-2xl -translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-background/60 mb-8"
          >
            <Link to={ROUTE_PATHS.HOME} className="hover:text-primary transition-colors">
              {ui.breadcrumbHome}
            </Link>
            <span>/</span>
            <span className="text-background/90">{ui.breadcrumbCurrent}</span>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeInUp} className="mb-4">
              <span className="inline-block px-3 py-1 bg-primary/20 text-primary text-xs font-semibold tracking-widest rounded-full uppercase">
                {ui.badge}
              </span>
            </motion.div>
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-background mb-6 leading-tight"
            >
              {ui.heroTitle}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-background/70 max-w-2xl leading-relaxed">
              {ui.heroSubtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* News list */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={stagger}
            className="space-y-6"
          >
            {sorted.map((article) => (
              <motion.div key={article.id} variants={fadeInUp}>
                <Link
                  to={`${ROUTE_PATHS.NEWS}/${article.id}`}
                  className="group flex gap-5 bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/40 hover:shadow-md transition-all duration-200"
                >
                  {/* Thumbnail */}
                  <div className="w-36 md:w-48 flex-shrink-0 relative overflow-hidden bg-muted">
                    <img
                      src={article.image}
                      alt={article.personName}
                      className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 py-5 pr-5 flex flex-col justify-center gap-2">
                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full">
                        {article.tag[lang]}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(article.date, lang)}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5" />
                        {article.location[lang]}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-base md:text-lg font-bold text-foreground leading-snug group-hover:text-primary transition-colors">
                      {article.title[lang]}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                      {article.excerpt[lang]}
                    </p>

                    {/* Read more */}
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary mt-1 group-hover:gap-2 transition-all">
                      {ui.readMore}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
